import { PrinterRow } from "components/Modals/SettingsModal/SettingsPanel";
import { LOCAL_STORAGE_KEYS } from "components/Viewer3d/consts";
import { GCodeGenerator } from "components/Viewer3d/GCodeGenerator";
import { useDesignStore } from "context/DesignContext";
import { ConditionSide } from "models/Insole";
import toast from "react-hot-toast";
import { fetchAllPrinters, saveGCodeSetting, storeDefaultPrinters } from "./Printerservice";
import ApiClient from "ApiClient";
import { User } from "models/User";
import { InsoleDesign } from "models/InsoleDesign";
import { SaveSettings, scheduleAutosave } from "components/Viewer3d/SaveManager";
import { Printer } from "models/Printer";
import { useViewerStore, ViewerTabsMode } from "context/ViewerContext";
import { canEditDesign } from "auth/permissions";

function emitGcodeVoice(ok: boolean, started: boolean, message: string, openSettings = false) {
    document.dispatchEvent(new CustomEvent("voice:gcodeResult", {
        detail: { ok, started, message, openSettings },
    }));
}


const saveGCodeSettings = async (api: ApiClient, regionalEnabledRight: boolean, regionalEnabledLeft: boolean) => {
    const leftGCodeSetting = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() == 'left');
    const storedDefaultPrinters: any[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS) ?? '[]');
    var pLeft: Printer | null = null;
    var pRight: Printer | null = null;

    if (storedDefaultPrinters && storedDefaultPrinters.length > 0) {
        pLeft = storedDefaultPrinters.find(p => p.defaultPrinter === ConditionSide.left || p.defaultPrinter === ConditionSide.both) ?? null
        pRight = storedDefaultPrinters.find(p => p.defaultPrinter === ConditionSide.right || p.defaultPrinter === ConditionSide.both) ?? null
    }
    if (leftGCodeSetting && pLeft) {
        leftGCodeSetting.printerId = pLeft.printerId;
        leftGCodeSetting.insoleHardness = null;
        saveGCodeSetting(api, leftGCodeSetting);
    }
    const rightGCodeSetting = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() == 'right');
    if (rightGCodeSetting && pRight) {
        rightGCodeSetting.printerId = pRight.printerId;
        rightGCodeSetting.insoleHardness = null;
        saveGCodeSetting(api, rightGCodeSetting);
    }
};

const storeSaveAsNewForAdmin = (saveAsNewForAdmin: boolean | null) => {
    if (saveAsNewForAdmin === null) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN);
        return;
    }
    localStorage.setItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN, saveAsNewForAdmin.toString());
}

const restoreSaveAsNewForAdmin = (): boolean | null => {
    const saveAsNewForAdmin = localStorage.getItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN);
    if (saveAsNewForAdmin === null || saveAsNewForAdmin === '') return null;
    return saveAsNewForAdmin === 'true';
}

const hasRegionalSettings = (gcodeSetting: any) => {
    const notNull = gcodeSetting?.foreFootHardness !== '' &&
    gcodeSetting?.midInnerHardness !== '' &&
    gcodeSetting?.midOuterHardness !== '' &&
    gcodeSetting?.heelHardness !== '' &&
    gcodeSetting?.foreFootHardness !== null &&
    gcodeSetting?.midInnerHardness !== null &&
    gcodeSetting?.midOuterHardness !== null &&
    gcodeSetting?.heelHardness !== null;
    const regional =
    !(gcodeSetting.foreFootHardness === gcodeSetting.midInnerHardness &&
        gcodeSetting.midInnerHardness === gcodeSetting.midOuterHardness &&
        gcodeSetting.midOuterHardness === gcodeSetting.heelHardness);

    return notNull && regional;
}

export const prepareGCode = async (
    api: ApiClient,
    user: User | null,
    insoleDesign: InsoleDesign | null,
) => {
    const errors: string[] = [];
    let needsSettings = false;
    const leftGCodeSettings = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() == 'left');
    const leftIsRegionalHardness = hasRegionalSettings(leftGCodeSettings);
    if (leftGCodeSettings && !leftIsRegionalHardness) leftGCodeSettings.insoleHardness = leftGCodeSettings.foreFootHardness;

    if (leftGCodeSettings) {
        if (!leftGCodeSettings.insoleHardness && !leftIsRegionalHardness) {
            errors.push('Left insole hardness is not selected.');
        }
    }

    const rightGCodeSettings = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() == 'right');
    const rightIsRegionalHardness = hasRegionalSettings(rightGCodeSettings);
    if (rightGCodeSettings && !rightIsRegionalHardness) rightGCodeSettings.insoleHardness = rightGCodeSettings.foreFootHardness;
    if (rightGCodeSettings) {
        if (!rightGCodeSettings.insoleHardness && !rightIsRegionalHardness) {
            errors.push('Right insole hardness is not selected.');
        }
    }

    const storedDefaultPrinters: any[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DEFAULT_PRINTERS) ?? '[]');
    var pLeft: PrinterRow | null = null;
    var pRight: PrinterRow | null = null;

    if (storedDefaultPrinters && storedDefaultPrinters.length > 0) {
        pLeft = storedDefaultPrinters.find(p => p.defaultPrinter === ConditionSide.left || p.defaultPrinter === ConditionSide.both) ?? null
        pRight = storedDefaultPrinters.find(p => p.defaultPrinter === ConditionSide.right || p.defaultPrinter === ConditionSide.both) ?? null
    }
    else {
    const printers = await fetchAllPrinters(api);
    pLeft = printers.find(p => p.defaultPrinter === ConditionSide.left || p.defaultPrinter === ConditionSide.both) || null;
    pRight = printers.find(p => p.defaultPrinter === ConditionSide.right || p.defaultPrinter === ConditionSide.both) || null;

    if (pLeft && pRight) {
        storeDefaultPrinters(pLeft, pRight);
    }
    else {
        toast.error('No printer found.');
        emitGcodeVoice(false, false, "No printer found. Open Settings, add a printer, set left and right defaults, then call generateGCode again. Do not say G-code is generating.", true);
        return;
    }
    (pLeft as any).settingName = pLeft?.setting.name;
    (pRight as any).settingName = pRight?.setting.name;
    }

    if (!pLeft?.printerId) {
        errors.push('Left default printer is missing.');
        needsSettings = true;
    }
    if (!pRight?.printerId) {
        errors.push('Right default printer is missing.');
        needsSettings = true;
    }

    if (errors.length > 0) {
        errors.forEach((error) => toast.error(error, { duration: 10000 }));
        const next = needsSettings
            ? " Open Settings, fix printers and defaults, then call generateGCode again."
            : " Fix the hardness in Automatic Design, then call generateGCode again.";
        emitGcodeVoice(false, false, `G-code did not start. ${errors.join(" ")}${next} Do not say G-code is generating.`, needsSettings);
        return;
    }

    emitGcodeVoice(true, true, "G-code generation started. When it finishes, the Print confirmation panel opens. Then the user can accept or cancel with confirmPrint.");

    // Ignore the clone design dialog for Admin
    if (user?.isAdmin) {
        const saveAsNewForAdmin = restoreSaveAsNewForAdmin();
        storeSaveAsNewForAdmin(false);
        if (insoleDesign?.designType !== 'Template')
            await scheduleAutosave('Current design', { now: true } as Partial<SaveSettings>);
        storeSaveAsNewForAdmin(saveAsNewForAdmin);
    } else {
    if (insoleDesign?.designType !== 'Template')
        await scheduleAutosave('Current design', { now: true } as Partial<SaveSettings>);
    }
    // Hub admins print member designs without edit rights; persist would 403 and is unnecessary for G-code.
    if (canEditDesign(user, insoleDesign?.createdBy?.id)) {
        await saveGCodeSettings(api, rightIsRegionalHardness, leftIsRegionalHardness);
    }
    useViewerStore.getState().setMode(ViewerTabsMode.GCode);
    await new Promise(resolve => setTimeout(resolve, 0));

    useDesignStore.getState().setGCode([]);
    try {
    let inputPrinters: { [side: string]: { id: string | undefined, settingName: string | undefined } } = {};
    // let inputPrinters : {[side: string]: string | undefined} = {};
    inputPrinters['right'] = { id: pRight?.printerId, settingName: pRight?.settingName };
    inputPrinters['left'] = { id: pLeft?.printerId, settingName: pLeft?.settingName };
    const inputHardness = [
        rightIsRegionalHardness ? [
        rightGCodeSettings?.foreFootHardness, rightGCodeSettings?.midInnerHardness, rightGCodeSettings?.midOuterHardness, rightGCodeSettings?.heelHardness
        ] : rightGCodeSettings?.foreFootHardness,
        leftIsRegionalHardness ? [
        leftGCodeSettings?.foreFootHardness, leftGCodeSettings?.midInnerHardness, leftGCodeSettings?.midOuterHardness, leftGCodeSettings?.heelHardness
        ] : leftGCodeSettings?.foreFootHardness
    ];
    const gcodeGenerator = new GCodeGenerator(insoleDesign?.insoles, inputHardness, inputPrinters);
    const gcodes = await gcodeGenerator.GenerateGCode();
    if (gcodes == null || gcodes.length <= 0) throw new Error();
        useDesignStore.getState().setGCode(gcodes);
        useDesignStore.setState({ GcodeReady: true });
    } catch {
        useDesignStore.setState({ GcodeReady: false });
        emitGcodeVoice(false, true, "G-code generation failed after it started. Check the design and printer settings, then call generateGCode again.");
    }
};